import {Alert, Paper, SimpleGrid, Stack, Table, Text, Title} from "@mantine/core";
import {WarningIcon} from "@phosphor-icons/react";
import {useQuery} from "@tanstack/react-query";
import {useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import api from "../../../utils/api.ts";
import ProductionModuleNav from "./ProductionModuleNav.tsx";
import {ProductionDashboardResponse} from "./types.ts";
import SyncStatus from "./SyncStatus.tsx";

function DashboardCard({label, value}: {label: string, value: string | number}) {
    return (
        <Paper withBorder p="md">
            <Text size="sm" c="dimmed">{label}</Text>
            <Title order={3}>{value}</Title>
        </Paper>
    );
}

function ProductionModuleDashboardPage() {
    const {moduleId} = useParams<{moduleId?: string}>();

    const query = useQuery<ProductionDashboardResponse>({
        queryKey: ['prod-module', moduleId, 'dashboard'],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/dashboard`)).data as ProductionDashboardResponse,
        enabled: !!moduleId,
    });

    if (!moduleId) {
        return <Page><Alert color="red" title="Missing module ID">No module selected.</Alert></Page>;
    }

    if (query.isPending) {
        return <LoaderScreen message="Loading dashboard"/>;
    }

    if (query.isError || !query.data) {
        return <Page><Alert color="red" title="Module unavailable">Could not load dashboard data.</Alert></Page>;
    }

    const {module, stats, missingDatacards} = query.data;


    return (
        <Page>
            <title>{`${module.label} - Dashboard`}</title>
            <Stack gap="md">
                <div>
                    <Title order={1}>{module.label}</Title>
                    <Text c="dimmed">Module ID: {module.id}</Text>
                </div>

                <ProductionModuleNav moduleId={moduleId}/>

                {missingDatacards.length > 0 && (
                    <Alert color="red" icon={<WarningIcon />} title="Missing datacard assignments">
                        <Stack gap="xs">
                            <Text size="sm">
                                {stats.missingDatacardAssignments} location{stats.missingDatacardAssignments === 1 ? "" : "s"} currently
                                producing or queued to produce an entity with no matching datacard assigned:
                            </Text>
                            <Table withTableBorder withColumnBorders>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Location</Table.Th>
                                        <Table.Th>Type</Table.Th>
                                        <Table.Th>Missing datacard for</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {missingDatacards.map((row) => (
                                        <Table.Tr key={`${row.entityId}:${row.productId}`}>
                                            <Table.Td>{row.entityName}</Table.Td>
                                            <Table.Td>{row.entityType === "facility" ? "Facility" : "Station"}</Table.Td>
                                            <Table.Td>{row.productName || row.productId}</Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Stack>
                    </Alert>
                )}

                <SimpleGrid cols={{base: 1, md: 3}}>
                    <DashboardCard label="Tracked Entities" value={stats.trackedEntities}/>
                    <DashboardCard label="Running Actions" value={stats.runningActions}/>
                    <DashboardCard label="Queued Items" value={stats.queuedItems}/>
                    <DashboardCard label="Stations" value={stats.stations}/>
                    <DashboardCard label="Facilities" value={stats.facilities}/>
                    <DashboardCard label="Stale Entities" value={stats.staleEntities}/>
                </SimpleGrid>

                <SyncStatus
                    status={stats.syncStatus}
                    lastSync={stats.lastSyncAt}
                    />
            </Stack>
        </Page>
    );
}

export default withRouteErrorBoundary(ProductionModuleDashboardPage);
